import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FolderTree, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AdminPage,
  ConfirmActionDialog,
  EmptyState,
  SearchField,
  SectionCard,
  StatusBadge,
  formatNumber,
} from '../../../components/design-system/admin';
import { useProductFamiliesApi } from '../../../common/hooks/use-product-families-api.hook';
import {
  setProductFamilies,
  setProductFamiliesLoading,
  deleteProductFamily,
} from '../../../redux/productFamilies/productFamiliesActions';
import { ProductFamily } from '../../../common/models/product-family.model';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { useResolveTranslation } from '../../../common/hooks/use-resolve-translation.hook';
import { resolveTranslation } from '../../../common/utils/translatable-string';
import { CatalogRowActions } from '../../../modules/superadmin/catalog-config';

/**
 * Product families: the schemas products follow — which attributes they have,
 * how their variants vary, how they are visualized. A table with what tells
 * one family from another; each row opens the editor.
 */
export const ProductFamilies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rt } = useResolveTranslation();
  const productFamiliesApi = useProductFamiliesApi();

  const { families, loading } = useSelector(
    (state: VendorState) => state.productFamilies
  );

  const [filter, setFilter] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState<ProductFamily | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Fetch families on mount
  const fetchFamilies = useCallback(async () => {
    setLoadFailed(false);
    dispatch(setProductFamiliesLoading(true));
    try {
      const data = await productFamiliesApi.findAllFamilies();
      dispatch(setProductFamilies(data));
    } catch (error) {
      console.error('Error fetching product families:', error);
      // The spinner used to stay forever on a failed load.
      dispatch(setProductFamiliesLoading(false));
      setLoadFailed(true);
    } finally {
      setFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  // Filter families based on search (guard against undefined entries)
  const query = filter.trim().toLowerCase();
  const filteredFamilies = (families || []).filter(
    family => {
      if (!family || !family.name) return false;
      if (!query) return true;
      const haystack = [
        resolveTranslation(family.name, 'en'),
        resolveTranslation(family.description, 'en'),
        rt(family.name),
        family.slug || '',
      ];
      return haystack.some(s => s.toLowerCase().includes(query));
    }
  );

  const handleAddFamily = () => {
    navigate('/product-families/new');
  };

  const handleEditFamily = (family: ProductFamily) => {
    navigate(`/product-families/${family._id}/edit`);
  };

  const handleDeleteClick = (family: ProductFamily) => {
    if (family.isSystemFamily) {
      // System families cannot be deleted
      return;
    }
    setFamilyToDelete(family);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!familyToDelete) return;

    setDeleting(true);
    try {
      await productFamiliesApi.deleteFamily(familyToDelete._id);
      dispatch(deleteProductFamily(familyToDelete._id));
      setShowDeleteDialog(false);
      setFamilyToDelete(null);
      toast.success(t('ADMIN_CATALOG.FAMILIES.DELETED'));
    } catch (error) {
      console.error('Error deleting family:', error);
      toast.error(t('ADMIN_CATALOG.FAMILIES.DELETE_FAILED'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setFamilyToDelete(null);
  };

  const showSkeleton = loading || (!fetched && (families || []).length === 0);
  const total = (families || []).filter(f => f && f.name).length;

  const addButton = (
    <Button onClick={handleAddFamily}>
      <Plus />
      {t('ADMIN_CATALOG.FAMILIES.ADD')}
    </Button>
  );

  return (
    <AdminPage
      title={t('ADMIN_SHELL.NAV.FAMILIES')}
      description={t('ADMIN_CATALOG.FAMILIES.DESCRIPTION')}
      actions={addButton}
    >
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchField
          value={filter}
          onChange={setFilter}
          placeholder={t('ADMIN_CATALOG.FAMILIES.SEARCH_PLACEHOLDER')}
        />
        {!showSkeleton && !loadFailed && total > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="families-count">
            {query
              ? t('ADMIN_CATALOG.COMMON.SHOWN_OF', { shown: filteredFamilies.length, total })
              : t('ADMIN_CATALOG.FAMILIES.COUNT', { count: total })}
          </span>
        )}
      </div>

      {loadFailed && !showSkeleton && total === 0 ? (
        <SectionCard>
          <EmptyState
            title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
            action={
              <Button variant="outline" onClick={fetchFamilies}>
                {t('ADMIN_SHELL.COMMON.RETRY')}
              </Button>
            }
          />
        </SectionCard>
      ) : showSkeleton ? (
        <SectionCard flush className="pt-0" testId="families-loading">
          <div className="flex flex-col divide-y">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="ms-auto h-4 w-16" />
              </div>
            ))}
          </div>
        </SectionCard>
      ) : filteredFamilies.length === 0 ? (
        <SectionCard>
          {query ? (
            <EmptyState
              icon={<FolderTree />}
              title={t('ADMIN_CATALOG.COMMON.NO_MATCH_TITLE', { query: filter.trim() })}
              description={t('ADMIN_CATALOG.COMMON.NO_MATCH_BODY')}
              action={
                <Button variant="outline" onClick={() => setFilter('')}>
                  {t('ADMIN_SHELL.COMMON.CLEAR')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<FolderTree />}
              title={t('ADMIN_CATALOG.FAMILIES.EMPTY_TITLE')}
              description={t('ADMIN_CATALOG.FAMILIES.EMPTY_BODY')}
              action={addButton}
            />
          )}
        </SectionCard>
      ) : (
        <SectionCard flush className="pt-0" testId="families-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="ps-4 text-start">{t('ADMIN_CATALOG.FAMILIES.COL_FAMILY')}</TableHead>
                <TableHead className="text-start">{t('ADMIN_CATALOG.COMMON.COL_SLUG')}</TableHead>
                <TableHead className="text-end">{t('ADMIN_CATALOG.FAMILIES.COL_ATTRIBUTES')}</TableHead>
                <TableHead className="text-end">{t('ADMIN_CATALOG.FAMILIES.COL_AXES')}</TableHead>
                <TableHead className="text-start">{t('ADMIN_CATALOG.FAMILIES.COL_VISUALIZATION')}</TableHead>
                <TableHead className="text-start">{t('ADMIN_CATALOG.COMMON.COL_STATUS')}</TableHead>
                <TableHead className="w-12 pe-4">
                  <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFamilies.map(family => {
                const name = rt(family.name);
                const description = rt(family.description);
                const axesTotal = family.variantAxes?.length || 0;
                const axesOn = family.variantAxes?.filter(a => a.enabled)?.length || 0;
                const seamless = family.visualizationConfig?.requiresSeamlessTexture;
                const model3d = family.visualizationConfig?.requires3DModel;
                return (
                  <TableRow
                    key={family._id}
                    className={cn('cursor-pointer', !family.isActive && 'text-muted-foreground')}
                    onClick={() => handleEditFamily(family)}
                    data-testid="family-row"
                  >
                    <TableCell className="max-w-md ps-4 whitespace-normal">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Link
                          to={`/product-families/${family._id}/edit`}
                          onClick={e => e.stopPropagation()}
                          className="text-sm font-medium hover:underline"
                        >
                          {name}
                        </Link>
                        {family.isSystemFamily && <Badge variant="outline">{t('ADMIN_CATALOG.COMMON.SYSTEM')}</Badge>}
                        {family.isDefault && <Badge variant="outline">{t('ADMIN_CATALOG.COMMON.DEFAULT')}</Badge>}
                      </div>
                      <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                        {description || t('ADMIN_CATALOG.FAMILIES.NO_DESCRIPTION')}
                      </p>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{family.slug}</TableCell>
                    <TableCell className="text-end text-sm tabular-nums">
                      {formatNumber(family.attributes?.length || 0)}
                    </TableCell>
                    <TableCell
                      className="text-end text-sm tabular-nums"
                      title={t('ADMIN_CATALOG.FAMILIES.AXES_HINT', { on: axesOn, total: axesTotal })}
                    >
                      {formatNumber(axesOn)}
                      {axesTotal > axesOn && (
                        <span className="text-muted-foreground"> / {formatNumber(axesTotal)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {seamless || model3d ? (
                        <div className="flex flex-wrap gap-1">
                          {seamless && <Badge variant="secondary">{t('ADMIN_CATALOG.FAMILIES.SEAMLESS')}</Badge>}
                          {model3d && <Badge variant="secondary">{t('ADMIN_CATALOG.FAMILIES.MODEL_3D')}</Badge>}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge tone={family.isActive ? 'success' : 'neutral'}>
                        {family.isActive ? t('ADMIN_CATALOG.COMMON.ACTIVE') : t('ADMIN_CATALOG.COMMON.INACTIVE')}
                      </StatusBadge>
                    </TableCell>
                    {/* The menu's clicks (portalled, but bubbling through React) must not open the row. */}
                    <TableCell className="pe-4 text-end" onClick={e => e.stopPropagation()}>
                      <CatalogRowActions
                        name={name}
                        onEdit={() => handleEditFamily(family)}
                        onDelete={() => handleDeleteClick(family)}
                        deleteBlockedReason={
                          family.isSystemFamily ? t('ADMIN_CATALOG.FAMILIES.SYSTEM_LOCKED') : undefined
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmActionDialog
        open={showDeleteDialog}
        onOpenChange={open => !open && handleCancelDelete()}
        title={t('ADMIN_CATALOG.FAMILIES.DELETE_TITLE')}
        description={t('ADMIN_CATALOG.FAMILIES.DELETE_DESCRIPTION', { name: rt(familyToDelete?.name) })}
        confirmLabel={t('ADMIN_CATALOG.FAMILIES.DELETE_CONFIRM')}
        destructive
        busy={deleting}
        onConfirm={handleConfirmDelete}
      />
    </AdminPage>
  );
};

export default ProductFamilies;
